import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { useMemo, useState } from "react";
import { useLabellingLocations } from "hooks/useLabellingLocations";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import {
  LabellerDetails,
  useGetCostLabellersQuery,
  useGetDailyLabellerStatsQuery,
} from "src/store/api/generatedApi";
import { DateRangeFilterBar, useDateRangeFilters } from "./dateRangeFilterBar";
import {
  DailyActivityView,
  DailyStatsForTenant,
  useDailyActivityViewState,
} from "./dailyActivityView";

const NO_LABELLERS: readonly LabellerDetails[] = [];

export const DailyActivityTab = () => {
  const { t } = useTenantTranslation();
  const tenantName = useTenant();
  const dateFilters = useDateRangeFilters();
  const [location, setLocation] = useState<string>("");
  const viewState = useDailyActivityViewState();
  const [selectedLabeller, setSelectedLabeller] =
    useState<LabellerDetails | null>(null);

  const { locationNames } = useLabellingLocations();

  const filterQueryParams = useMemo(
    () => ({
      tenantName,
      start: dateFilters.start?.toISOString() ?? undefined,
      end: dateFilters.end?.toISOString() ?? undefined,
      filterMode: dateFilters.filterMode,
      location: location || undefined,
    }),
    [tenantName, dateFilters, location]
  );

  const { data: labellers, isLoading: labellersLoading } =
    useGetCostLabellersQuery(filterQueryParams);

  const sharedQueryParams = useMemo(
    () => ({
      ...filterQueryParams,
      labellerId: selectedLabeller?.id ?? "",
    }),
    [filterQueryParams, selectedLabeller]
  );

  const {
    data: labellingData,
    isLoading: labellingLoading,
    isFetching: labellingFetching,
  } = useGetDailyLabellerStatsQuery(
    { ...sharedQueryParams, sessionTypes: ["labelling"] },
    { skip: !selectedLabeller || !viewState.showLabelling }
  );

  const {
    data: reviewingData,
    isLoading: reviewingLoading,
    isFetching: reviewingFetching,
  } = useGetDailyLabellerStatsQuery(
    { ...sharedQueryParams, sessionTypes: ["reviewing"] },
    { skip: !selectedLabeller || !viewState.showReviewing }
  );

  const labellingByTenant = useMemo<DailyStatsForTenant[]>(
    () => [{ tenantName, dailyStats: labellingData?.daily_stats ?? [] }],
    [tenantName, labellingData?.daily_stats]
  );
  const reviewingByTenant = useMemo<DailyStatsForTenant[]>(
    () => [{ tenantName, dailyStats: reviewingData?.daily_stats ?? [] }],
    [tenantName, reviewingData?.daily_stats]
  );

  const locationFilter = (
    <FormControl size="small" sx={{ minWidth: 200 }}>
      <InputLabel id="daily-activity-location-filter-label">
        {t("location")}
      </InputLabel>
      <Select
        labelId="daily-activity-location-filter-label"
        value={location}
        label={t("location")}
        onChange={(e) => {
          setLocation(e.target.value);
        }}
      >
        <MenuItem value="">{t("all")}</MenuItem>
        {locationNames.map((name) => (
          <MenuItem key={name} value={name}>
            {name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <DailyActivityView
      labellers={labellers ?? NO_LABELLERS}
      labellersLoading={labellersLoading}
      selectedLabeller={selectedLabeller}
      onSelectLabeller={setSelectedLabeller}
      labellingByTenant={labellingByTenant}
      reviewingByTenant={reviewingByTenant}
      isLoading={labellingLoading || reviewingLoading}
      isFetching={labellingFetching || reviewingFetching}
      state={viewState}
      toolbarLeading={
        <>
          <DateRangeFilterBar filters={dateFilters} />
          {locationFilter}
        </>
      }
    />
  );
};
